/**
 * Кнопка «Готов / Перерыв» и объяснение, почему пульт пуст (план 2.1).
 *
 * «Готов» — это согласие получать контакты, а не признак того, что человек за
 * компьютером: раздача смотрит именно на этот флаг (§6.2). Пока он не нажат,
 * диспетчера пропускают молча, и пульт выглядит сломанным.
 *
 * Состояние линии здесь берётся с СЕРВЕРА, а не из браузерного софтфона. Эти два
 * взгляда расходятся: браузер уже зарегистрировался, а телефония ещё не прислала
 * PeerStatusChange — и раздача в этот момент всё ещё пропускает диспетчера. Показывать
 * надо то, по чему принимается решение, иначе человек видит зелёную линию и пустой пульт.
 *
 * Причину считает сервер одной функцией на всю систему (dispatcherCapacity), здесь
 * она только переводится в текст: свой расчёт в браузере разошёлся бы с раздачей.
 */

import type { BlockedReason } from '@cc/shared';

import { Button } from '../../shared/ui/button.js';
import { blockedReasonHint, blockedReasonTitle } from '../../shared/lib/blocked-reason.js';

export interface ReadyToggleProps {
  /** Нажата ли кнопка готовности — значение из снапшота, а не локальное */
  ready: boolean;
  /** Линия по мнению телефонии: именно её смотрит раздача */
  sipRegistered: boolean;
  /** Первая сработавшая причина простоя или null, если раздача диспетчера видит */
  blockedReason: BlockedReason | null;
  /**
   * Готовность переключает только диспетчер: маршрут /desk/ready отвечает
   * администратору и супервизору 403, и кнопка, которая всегда падает, хуже её отсутствия
   */
  canToggle: boolean;
  busy: boolean;
  /** Текст отказа сервера: без него нажатие «не сработало» выглядит как зависание */
  error: string | undefined;
  onToggle: (ready: boolean) => void;
  /**
   * Счётчики с сервера. Название причины без цифр диспетчер читает как поломку:
   * «Держит открытые карточки» — это про него самого или про систему? Цифры
   * отвечают на вопрос сразу.
   */
  openNow: number;
  maxOpen: number;
  usedToday: number;
  dailyLimit: number;
}

export function ReadyToggle({
  ready,
  sipRegistered,
  blockedReason,
  canToggle,
  busy,
  error,
  onToggle,
  openNow,
  maxOpen,
  usedToday,
  dailyLimit,
}: ReadyToggleProps): React.JSX.Element {
  // Плашка окрашивается по факту раздачи, а не по кнопке: «Готов» нажат, но линии
  // нет — это не рабочее состояние, и зелёным его показывать нельзя
  const working = blockedReason === null;

  // Компактно (просьба Evgeniy 30.08.2026): плашка — служебная строка, а не
  // главный блок экрана. Рабочее состояние — одна зелёная строка; простой —
  // строка с причиной плюс мелкая подсказка «что сделать»
  return (
    <section
      className={
        'flex flex-col gap-1 rounded-xl border px-4 py-2 ' +
        (working ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50')
      }
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* Кнопка первой в строке (10.09.2026): у новых диспетчеров она стояла
            справа за текстом, и при зуме или узком окне уезжала из виду —
            «нет кнопки вернуться в работу, контакты не раздались» */}
        {canToggle ? (
          <Button
            variant={ready ? 'secondary' : 'primary'}
            busy={busy}
            className="shrink-0"
            onClick={() => {
              onToggle(!ready);
            }}
          >
            {ready ? 'Уйти на перерыв' : 'Вернуться в работу'}
          </Button>
        ) : null}
        <span
          className={
            'h-2 w-2 shrink-0 rounded-full ' + (working ? 'bg-emerald-500' : 'bg-amber-500')
          }
          aria-hidden
        />
        <span className="text-sm font-semibold text-slate-900">
          {working
            ? 'Вы в работе — контакты раздаются'
            : headline(blockedReason, openNow, maxOpen, usedToday, dailyLimit)}
        </span>
        {/* Только то, чего не хватает: «в работе · линия готова» рядом с зелёной
            строкой было шумом, а «в работе · линии нет» читалось как противоречие */}
        {!ready || !sipRegistered ? (
          <span className="text-sm text-slate-500">
            {[!ready && blockedReason !== 'not_ready' ? 'кнопка «Готов» не нажата' : null, !sipRegistered ? 'линии нет' : null]
              .filter((part) => part !== null)
              .join(' · ')}
          </span>
        ) : null}

        {canToggle ? null : (
          <span className="ml-auto text-xs text-slate-500">
            Готовность переключает сам диспетчер
          </span>
        )}
      </div>

      {blockedReason === null ? null : (
        <p className="pl-5 text-xs text-slate-600">{hint(blockedReason, openNow, maxOpen)}</p>
      )}

      {error === undefined ? null : (
        <p role="alert" className="pl-5 text-xs text-red-700">
          {error} Готовность не переключилась — состояние выше показано по данным сервера.
        </p>
      )}
    </section>
  );
}

/**
 * Заголовок простоя.
 *
 * Общая таблица названий (blockedReasonTitle) остаётся источником слов для
 * админских экранов, но диспетчеру нужна не карточка состояния, а ответ на
 * «что мне сейчас сделать». Поэтому для случая с открытыми карточками —
 * с цифрами: «Держит открытые карточки» без чисел выглядит как чужая
 * формулировка про кого-то другого.
 */
function headline(
  reason: BlockedReason,
  openNow: number,
  maxOpen: number,
  usedToday: number,
  dailyLimit: number,
): string {
  if (reason === 'max_open' && maxOpen > 0) {
    return `Новые контакты не придут: у вас ${openNow} ${cards(openNow)} из ${String(maxOpen)}`;
  }
  // Цифры лимита — только когда лимит и есть причина: в остальное время это
  // админская механика, диспетчеру она не нужна (просьба Evgeniy 30.08.2026)
  if (reason === 'daily_limit' && dailyLimit > 0) {
    return `На сегодня всё: выдано ${String(usedToday)} из ${String(dailyLimit)} новых номеров`;
  }
  return `Контакты вам не раздаются: ${blockedReasonTitle(reason)}`;
}

function hint(reason: BlockedReason, openNow: number, maxOpen: number): string {
  if (reason === 'max_open' && maxOpen > 0) {
    return (
      `Больше ${String(maxOpen)} карточек одновременно держать нельзя. ` +
      'Проставьте метку результата в любой из открытых — следующий контакт придёт сам, ' +
      'без нажатий.'
    );
  }
  return blockedReasonHint(reason);
}

/** Склонение: «1 карточка», «2 карточки», «5 карточек» */
function cards(n: number): string {
  const tail = n % 100;
  if (tail >= 11 && tail <= 14) return 'карточек';
  switch (n % 10) {
    case 1:
      return 'карточка';
    case 2:
    case 3:
    case 4:
      return 'карточки';
    default:
      return 'карточек';
  }
}
